import { useSiteSettingsContext } from '@context/SiteSettingsContext'
import Hero from '@global/Blocks/Hero'
import Button from '@global/Button'
import EnquiryForm from '@global/Form/EnquiryForm'
import FormNote from '@global/Form/FormNote'
import Field from '@global/FormInput/Field'
import RadioGroup from '@global/FormInput/RadioGroup'
import IconArrowRight from '@icons/ArrowRight'
import { adfenix } from '@lib/adfenixHelper'
import { logEvent } from '@lib/analyticsService'
import {
  appraisalRequest,
  getThankYouPageData,
} from '@lib/api-services/appraisalService'
import { apis } from '@lib/apiUrls'
import facebookPixel from '@lib/facebookPixelHelper'
import { GooglePlacesToAppraisalAddress } from '@lib/helpers'
import { ApiKeys } from '@lib/variables'
import { Router } from '@routes'
import { Col, Container, Row } from '@styles/global.styled'
import AgentMobileNavBarBottom from 'components/Agents/AgentProfile/AgentInfo/AgentMobileNavBarBottom'
import { detect } from 'detect-browser'
import { Formik } from 'formik'
import { parsePhoneNumberWithError } from 'libphonenumber-js'
import dynamic from 'next/dynamic'
import nProgress from 'nprogress'
import { useEffect, useState } from 'react'
import { loadReCaptcha } from 'react-recaptcha-v3'
import { toast } from 'react-toastify'
import * as Yup from 'yup'
import * as S from './AppraisalFormMain.styled'

const ReCaptcha = dynamic(
  () => import('react-recaptcha-v3').then((mod) => mod.ReCaptcha),
  { ssr: false }
)

const GooglePlaceAutoComplete = dynamic(
  () => import('@global/FormInput/GooglePlaceAutoComplete'),
  { ssr: false }
)

const validationSchema = Yup.object().shape({
  property_type: Yup.string().required('Property type is required'),
  first_name: Yup.string().required('First name is required'),
  last_name: Yup.string().required('Last name is required'),
  notes: Yup.string(),
  phone: Yup.string()
    .required('Phone Number is required')
    .test('is-valid', 'Please enter a valid phone number', function (value) {
      if (!value)
        return this.createError({ message: 'Phone Number is required' })
      if (value.indexOf('+61') > -1) {
        value = value.replace(/ /g, '').replace('+61', '')
      }
      const phoneNumber = parsePhoneNumberWithError(value, 'AU')
      if (phoneNumber) {
        if (phoneNumber.isValid()) {
          return true
        } else if (phoneNumber.isPossible()) {
          if (
            phoneNumber.nationalNumber.length == 8 &&
            phoneNumber.countryCallingCode == 61
          ) {
            return true
          }
          return false
        }
      }
      return false
    }),
  email: Yup.string()
    .email('Enter a valid email')
    .required('Email Address is required'),
  appraisal_type: Yup.string().required('Appraisal Type is required'),
  address_full_text: Yup.string().required('Address is required'),
  address_postcode: Yup.string().required('Address is required'),
  address_suburb: Yup.string().required('Address is required'),
})

const AppraisalForm = (props) => {
  const {
    backgroundImage,
    referral_channel,
    referral_source,
    title,
    copy,
    office_agent_slug,
    thank_you_page_path,
    agent,
  } = props
  const siteContext = useSiteSettingsContext()
  const [thankYouPagePath, setThankYouPagePath] = useState(thank_you_page_path)

  let queryParams = new URLSearchParams(window.location.search)

  useEffect(() => {
    const fetchThankYou = async () => {
      if (siteContext?.settings?.appraisalsettings?.thank_you_page) {
        const result = await getThankYouPageData(
          apis.WAGTAIL_API_BASE +
            siteContext.settings.appraisalsettings.thank_you_page
        )
        setThankYouPagePath(result?.meta?.path)
      }
    }

    if (!thank_you_page_path) {
      fetchThankYou()
    } else {
      setThankYouPagePath(thank_you_page_path)
    }
  }, [thank_you_page_path])

  const onSubmit = async (values, { setSubmitting }) => {
    if (referral_channel) {
      values.referral_channel = referral_channel
    }

    //check bot spam when the credit_card field is filled
    if (values.credit_card) {
      return
    }

    // Now set the user details for debugging
    const browser = detect()
    const user_agent = `${browser.name} ${browser.version} ${browser.os}`
    values['user_agent'] = user_agent
    if (office_agent_slug) {
      values['office_agent_slug'] = office_agent_slug
    }

    setSubmitting(true)

    //tracking contact request by Adfenix
    adfenix.bookappraisal()
    nProgress.start()
    const res = await appraisalRequest(values)
    if (res.ok) {
      nProgress.done()
      // GA: log form submission in google analytics
      logEvent('Forms', 'Submit appraisal request', values.property_type)

      facebookPixel.trackLead({
        type: 'appraisal',
        title: 'Submit appraisal request',
      })

      // move to thank you
      Router.pushRoute(`${thankYouPagePath}`)
      setSubmitting(false)
    } else {
      nProgress.done()
      toast.error('Some thing went wrong, please try again later')
      setSubmitting(false)
    }
  }

  useEffect(() => {
    if (!window.RECAPTCHA_INITIALIZED) {
      loadReCaptcha(ApiKeys.RECAPTCHA_KEY)
      window.RECAPTCHA_INITIALIZED = true
    }
  }, [])

  return (
    <>
      <Hero title={title} bgImageUrl={backgroundImage} />
      <S.AppraisalForm>
        <Container>
          <Row gutter={28}>
            <Col md={5}>
              <S.AppraisalFormTitle>
                Uncover the potential of your property in a few easy clicks
              </S.AppraisalFormTitle>
              <S.AppraisalFormDes>{copy}</S.AppraisalFormDes>
            </Col>
            <Col md={7}>
              <Formik
                onSubmit={onSubmit}
                initialValues={{
                  appraisal_type: 'Sale',
                  address_full_text: '',
                  property_type: '',
                  first_name: '',
                  last_name: '',
                  email: '',
                  phone: '',
                  notes: '',
                  address_postcode: '',
                  address_postcode: '',
                  agree_to_receive: true,
                }}
                validationSchema={validationSchema}
              >
                {({
                  values,
                  touched,
                  errors,
                  handleChange,
                  handleSubmit,
                  isSubmitting,
                  setFieldValue,
                  getFieldProps,
                  setValues,
                }) => (
                  <div>
                    <form noValidate onSubmit={handleSubmit}>
                      <input
                        type='text'
                        name='credit_card'
                        value={values['credit_card']}
                        style={{ display: 'none' }}
                        tabindex='-1'
                        autocomplete='off'
                        onChange={handleChange}
                      />
                      <Field
                        label='First Name'
                        {...getFieldProps('first_name')}
                        error={touched?.first_name && errors?.first_name}
                        required
                      />
                      <Field
                        label='Last Name'
                        {...getFieldProps('last_name')}
                        error={touched?.last_name && errors?.last_name}
                        required
                      />
                      <Row gutter={12}>
                        <Col md={6}>
                          <Field
                            label='Contact Email'
                            {...getFieldProps('email')}
                            error={touched?.email && errors?.email}
                            required
                          />
                        </Col>
                        <Col md={6}>
                          <Field
                            label='Contact Number'
                            {...getFieldProps('phone')}
                            error={touched?.phone && errors?.phone}
                            required
                          />
                        </Col>
                      </Row>
                      <RadioGroup
                        label='What kind of appraisal are you looking for?'
                        options={[
                          {
                            label: 'Sales',
                            value: 'Sale',
                          },
                          {
                            label: 'Rentals',
                            value: 'Rentals',
                          },
                          {
                            label: 'Both',
                            value: 'Both',
                          },
                        ]}
                        defaultValue='Sale'
                        {...getFieldProps('appraisal_type')}
                        error={
                          touched?.appraisal_type && errors?.appraisal_type
                        }
                      />
                      <GooglePlaceAutoComplete
                        placeholder='Please enter the address of the property you would like appraised.'
                        value={values['address_full_text']}
                        name='address_full_text'
                        onChange={(address) =>
                          setFieldValue('address_full_text', address, false)
                        }
                        onSelect={(_address, result) => {
                          if (result) {
                            setValues({
                              ...values,
                              ...GooglePlacesToAppraisalAddress(result),
                            })
                          } else {
                            setValues({
                              ...values,
                              address_postcode: '',
                              address_suburb: '',
                            })
                          }
                        }}
                        error={
                          !!(
                            touched?.address_full_text &&
                            (errors?.address_postcode ||
                              errors?.address_suburb ||
                              errors?.address_full_text)
                          ) && 'Please enter a valid address'
                        }
                        required
                      />
                      <Field
                        label='Lastly, what kind of property is this?'
                        type='select'
                        placeholder='Please select'
                        value={values.property_type}
                        error={touched?.property_type && errors?.property_type}
                        onChange={(val) => setFieldValue('property_type', val)}
                        options={[
                          {
                            value: '',
                            label: 'Please select',
                          },
                          {
                            value: 'House',
                            label: 'House',
                          },
                          {
                            value: 'Unit',
                            label: 'Unit',
                          },
                          {
                            value: 'Townhouse',
                            label: 'Townhouse',
                          },
                          {
                            value: 'Land',
                            label: 'Land',
                          },
                          {
                            value: 'Commercial',
                            label: 'Commercial',
                          },
                          {
                            value: 'Acreage',
                            label: 'Acreage',
                          },
                          {
                            value: 'Rural',
                            label: 'Rural',
                          },
                          {
                            value: 'Project/Development',
                            label: 'Project/Development',
                          },
                          {
                            value: 'Other',
                            label: 'Other',
                          },
                        ]}
                      />
                      <Field
                        type='textarea'
                        {...getFieldProps('notes')}
                        placeholder='Comments'
                      />

                      <S.AppraisalFormFooter>
                        <div>
                          <FormNote />
                          <div>
                            <Button
                              type='submit'
                              disabled={isSubmitting}
                              color='primary'
                              endIcon={<IconArrowRight />}
                            >
                              Request my appraisal
                            </Button>
                          </div>
                        </div>

                        <Field
                          type='checkbox'
                          {...getFieldProps('agree_to_receive')}
                          checked={values.agree_to_receive}
                          onChange={(e) => {
                            setFieldValue('agree_to_receive', e.target.checked)
                          }}
                          error={
                            touched?.agree_to_receive &&
                            errors?.agree_to_receive
                          }
                          label={
                            <span>
                              I{"'"}d like to receive occasional Barry Plant
                              Group email updates and insights
                            </span>
                          }
                        />
                      </S.AppraisalFormFooter>

                      <ReCaptcha
                        sitekey={ApiKeys.RECAPTCHA_KEY}
                        action='appraisal'
                        verifyCallback={(token) =>
                          setFieldValue('recaptcha', token, false)
                        }
                      />
                    </form>
                  </div>
                )}
              </Formik>
            </Col>
          </Row>
        </Container>
      </S.AppraisalForm>

      <AgentMobileNavBarBottom
        phone={agent?.phone}
        showGetInTouch={agent?.show_get_in_touch}
        showSendEnquiry={agent?.show_send_enquiry}
        active
      />

      <EnquiryForm
        type={'agent'}
        agentName={agent?.title}
        agentSlug={agent?.meta?.slug}
        headerInfo={{
          image: agent?.profile_image_thumbnail?.url,
          title: agent?.title,
          description: agent?.job_title,
          id: agent?.id,
        }}
      />
    </>
  )
}

AppraisalForm.defaultProps = {
  office_agent_slug: '',
  title: 'Request an Appraisal',
  backgroundImage: '/static/img/defaults/props/form-1-appraisal-form.jpg',
}

export default AppraisalForm
